import { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Check, X, ArrowRight } from '@/components/ui/Icons';
import { Logo } from '@/components/ui/Logo';
import { grantAnalyticsConsent, revokeAnalyticsConsent } from '@/lib/analytics';
import { cn } from '@/lib/cn';

type Decision = 'accepted' | 'rejected' | null;
type Prefs = {
  analytics: boolean;
  /** Strictly necessary cookies are always on. */
  necessary: true;
};

const STORAGE_KEY = 'bbc_cookie_consent_v1';

function readDecision(): { decision: Decision; prefs: Prefs } {
  if (typeof window === 'undefined') return { decision: null, prefs: { analytics: false, necessary: true } };
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { decision: null, prefs: { analytics: false, necessary: true } };
    const parsed = JSON.parse(raw) as { decision: Decision; prefs: Prefs };
    return parsed;
  } catch {
    return { decision: null, prefs: { analytics: false, necessary: true } };
  }
}

function writeDecision(decision: Decision, prefs: Prefs) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ decision, prefs, at: new Date().toISOString() }));
  } catch {
    // ignore quota / private mode failures
  }
}

export function CookieConsent() {
  const [open, setOpen] = useState(false);
  const [view, setView] = useState<'banner' | 'manage'>('banner');
  const [analyticsOn, setAnalyticsOn] = useState(false);

  // Decide whether to show the banner on first paint.
  useEffect(() => {
    const { decision, prefs } = readDecision();
    if (!decision) {
      setOpen(true);
    } else if (decision === 'accepted' && prefs.analytics) {
      // Returning visitor who already opted in: re-enable analytics.
      grantAnalyticsConsent();
    }
  }, []);

  // Re-open when the footer "Cookie settings" link fires the event.
  useEffect(() => {
    const handler = () => { setView('banner'); setOpen(true); };
    window.addEventListener('bbc:open-cookie-consent', handler);
    return () => window.removeEventListener('bbc:open-cookie-consent', handler);
  }, []);

  const accept = () => {
    const prefs: Prefs = { analytics: true, necessary: true };
    writeDecision('accepted', prefs);
    grantAnalyticsConsent();
    setOpen(false);
  };

  const reject = () => {
    const prefs: Prefs = { analytics: false, necessary: true };
    writeDecision('rejected', prefs);
    revokeAnalyticsConsent();
    setOpen(false);
  };

  const savePrefs = () => {
    const prefs: Prefs = { analytics: analyticsOn, necessary: true };
    writeDecision(analyticsOn ? 'accepted' : 'rejected', prefs);
    if (analyticsOn) grantAnalyticsConsent();
    else revokeAnalyticsConsent();
    setOpen(false);
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          key="cc-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="cc-title"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          className="fixed inset-0 z-70 flex items-center justify-center overflow-y-auto bg-navy-950/55 backdrop-blur-[3px] p-4 sm:p-6"
        >
          <motion.div
            key="cc-modal"
            initial={{ opacity: 0, y: 18, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 18, scale: 0.96 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className={cn(
              'relative w-full max-w-md sm:max-w-lg',
              'overflow-hidden rounded-2xl border border-gold-400/40 bg-navy-900 text-paper-0',
              'shadow-[0_24px_64px_-12px_rgba(0,0,0,0.7)] ring-1 ring-paper-0/5',
            )}
            // Stop the click bubbling to the overlay (which would close the modal in some
            // future variant - currently the overlay is non-dismissive but we still want
            // the inner scroll to work cleanly).
            onClick={(e) => e.stopPropagation()}
          >
            <div className="absolute inset-x-0 top-0 h-1 gold-divider" aria-hidden="true" />

            {/* Branded header strip */}
            <div className="flex flex-col items-center gap-3 bg-linear-to-b from-navy-900 to-navy-800 px-6 pt-7 pb-5 text-center">
              <Logo decorative className="h-12 sm:h-14" />
              <p className="text-[11px] font-semibold uppercase tracking-[0.28em] text-gold-300">
                Bliss Building Contractors
              </p>
            </div>

            {view === 'banner' ? (
              <div className="px-6 pt-5 pb-6 sm:px-7 sm:pb-7">
                <div className="text-center">
                  <h2 id="cc-title" className="font-display text-2xl font-semibold text-paper-0 sm:text-[26px]">
                    We use cookies
                  </h2>
                  <p className="mx-auto mt-2 max-w-sm text-sm leading-relaxed text-paper-0/80">
                    We use strictly-necessary cookies so the site works, and optional analytics
                    cookies (Google Analytics) so we can see what people find useful. Your choice
                    either way &mdash; we don&rsquo;t share anything with advertisers.
                  </p>
                </div>

                <div className="mt-6 grid grid-cols-1 gap-2 sm:grid-cols-3">
                  <button
                    type="button"
                    onClick={() => { setAnalyticsOn(false); setView('manage'); }}
                    className="order-3 rounded-md px-3 py-2.5 text-sm font-semibold text-paper-0/80 transition hover:text-gold-300 sm:order-1"
                  >
                    Manage
                  </button>
                  <button
                    type="button"
                    onClick={reject}
                    className="order-2 rounded-md border border-paper-0/20 px-4 py-2.5 text-sm font-semibold text-paper-0/90 transition hover:bg-navy-800"
                  >
                    Reject all
                  </button>
                  <button
                    type="button"
                    onClick={accept}
                    className="btn-gold order-1 px-5 py-2.5 text-sm sm:order-3"
                  >
                    Accept all <ArrowRight className="h-3 w-3" />
                  </button>
                </div>
              </div>
            ) : (
              <div className="p-5 sm:p-6">
                <h2 id="cc-title" className="font-display text-lg font-semibold text-paper-0">
                  Cookie preferences
                </h2>
                <p className="mt-1 text-sm leading-relaxed text-paper-0/80">
                  Pick what you’re comfortable with. You can change this any time using the
                  <strong> Cookie settings </strong> link in the footer.
                </p>

                <ul className="mt-4 space-y-3">
                  <li className="flex items-start gap-3 rounded-lg border border-paper-0/10 bg-navy-800/40 p-3">
                    <span className="mt-0.5 grid h-5 w-5 flex-none place-items-center rounded-full bg-gold-400 text-navy-900">
                      <Check className="h-3 w-3" />
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-semibold text-paper-0">Strictly necessary</p>
                      <p className="text-xs text-paper-0/70">
                        Session cookies for the admin login and basic site functionality. Always on.
                      </p>
                    </div>
                    <span className="text-xs font-semibold uppercase tracking-wider text-gold-300">On</span>
                  </li>

                  <li className="flex items-start gap-3 rounded-lg border border-paper-0/10 bg-navy-800/40 p-3">
                    <input
                      type="checkbox"
                      id="cc-analytics"
                      checked={analyticsOn}
                      onChange={(e) => setAnalyticsOn(e.target.checked)}
                      className="mt-1 h-4 w-4 cursor-pointer accent-gold-500"
                    />
                    <label htmlFor="cc-analytics" className="min-w-0 flex-1 cursor-pointer">
                      <p className="text-sm font-semibold text-paper-0">Analytics</p>
                      <p className="text-xs text-paper-0/70">
                        Google Analytics 4. Anonymised IP, no advertising cookies, no cross-site tracking.
                      </p>
                    </label>
                  </li>
                </ul>

                <div className="mt-5 flex flex-wrap items-center justify-end gap-2">
                  <button
                    type="button"
                    onClick={() => setView('banner')}
                    className="rounded-md px-3 py-2 text-sm font-semibold text-paper-0/85 hover:text-gold-300"
                  >
                    Back
                  </button>
                  <button
                    type="button"
                    onClick={savePrefs}
                    className="btn-gold px-5 py-2 text-sm"
                  >
                    Save preferences <Check className="h-3 w-3" />
                  </button>
                </div>
              </div>
            )}

            <button
              type="button"
              onClick={reject}
              className="absolute right-3 top-3 grid h-7 w-7 place-items-center rounded-full text-paper-0/70 transition hover:bg-navy-800 hover:text-paper-0"
              aria-label="Close cookie banner (rejects optional cookies)"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

/** Programmatically reopen the modal (footer "Cookie settings" link). */
export function openCookieConsent() {
  // Clearing the decision triggers the banner to show again on next mount.
  // We dispatch a custom event so a currently-mounted CookieConsent can react.
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
  window.dispatchEvent(new Event('bbc:open-cookie-consent'));
}
